import React from 'react';
import { Document, Page, Text, View, Link } from '@react-pdf/renderer';
import { createCommonStyles, formatDate, parseValue } from '../utils/pdfUtils';

const styles = createCommonStyles({
  footerContainer: {
    position: 'absolute',
    bottom: 30,
    left: 40,
    right: 40,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerLabel: {
    fontSize: 9,
    color: '#374151',
  },
  footerPage: {
    fontSize: 9,
    color: '#374151',
  },
});

// Props:
// - canvass: object with canvassName, canvassId, objective, timestamp, etc.
// - groupedInsights: { [tag: string]: Array<{ key, value, formattedKey }> }
// - extractedInsights: original insights object (optional)
const CanvassInsightsPDFDocument = ({ canvass = {}, groupedInsights = {}, tagWhitelist }) => {
  const now = new Date().toLocaleString();

  const allTags = Object.keys(groupedInsights || {});
  const tags = Array.isArray(tagWhitelist) && tagWhitelist.length > 0
    ? allTags.filter(t => tagWhitelist.includes(t))
    : allTags;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.title}>Extracted Insights</Text>
          <Text style={styles.subtitle}>Generated on: {now}</Text>
          <Text style={styles.subtitle}>
            {canvass.canvassName || 'Untitled'} ({canvass.canvassId || canvass.id || 'N/A'})
          </Text>
          {canvass.timestamp ? (
            <Text style={styles.subtitle}>Last Updated: {formatDate(canvass.timestamp)}</Text>
          ) : null}
        </View>

        {/* Overview */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Overview</Text>
          {canvass.objective ? (
            <>
            <Text style={styles.itemLabel}>Objective:</Text>
            <Text style={styles.itemValue}>{String(canvass.objective)}</Text>
            </>
          ) : null}
          {Array.isArray(canvass.sub_objectives) && canvass.sub_objectives.length > 0 ? (
            <View>
              <Text style={styles.itemLabel}>Sub Objective:</Text>
              {canvass.sub_objectives.map((item, idx) => (
                <Text key={`subobj-${idx}`} style={styles.itemValue}>
                  • {String(item)}
                </Text>
              ))}
            </View>
          ) : null}
          {canvass.interviewLink ? (
            <>
              <Text style={styles.itemLabel}>Interview Link:</Text>
              <Text style={styles.itemValue}>
                <Link src={String(canvass.interviewLink)}>{String(canvass.interviewLink)}</Link>
              </Text>
            </>
          ) : null}
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Introductions</Text>
          {Array.isArray(canvass.interview_introductions) && canvass.interview_introductions.length > 0 ? (
            <View>
              {canvass.interview_introductions.map((intro, idx) => (
                <>
                <Text style={styles.itemLabel}>Introduction {idx + 1}</Text>
                <Text
                  key={`intro-${idx}`}
                  style={{
                    ...styles.itemValue,
                    color: intro?.selected ? '#0d6efd' : (styles.itemValue?.color || undefined),
                  }}
                >
                  {String(intro?.introduction_text || '')}{intro?.selected ? ' (selected)' : ''}
                </Text>
                </>
              ))}
            </View>
          ) : null}
        </View>

        {/* Insights by Tag */}
        {tags.length > 0 ? (
          tags.map((tag) => (
            <View key={tag} style={styles.section}>
              <Text style={styles.sectionTitle}>{tag.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</Text>
              {(groupedInsights[tag] || []).map((item, idx) => {
                const parsed = parseValue(item.value);
                const valueText = Array.isArray(parsed)
                  ? parsed.join(', ')
                  : (typeof parsed === 'object' && parsed?.type === 'seed_question')
                    ? `Question: ${parsed.data.question}${parsed.data.rationale ? `\nRationale: ${parsed.data.rationale}` : ''}`
                    : String(parsed ?? '');
                return (
                  <View key={`${tag}-${idx}`} style={styles.itemContainer}>
                    <Text style={styles.itemLabel}>{item.formattedKey || item.key}</Text>
                    <Text style={styles.itemValue}>{valueText || 'N/A'}</Text>
                  </View>
                );
              })}
            </View>
          ))
        ) : (
          <Text style={styles.emptyState}>No insights available.</Text>
        )}

        {/* Footer */}
        <View fixed style={styles.footerContainer}>
          <Text style={styles.footerLabel}>August Says - Extracted Insights</Text>
          <Text style={styles.footerPage} render={({ pageNumber }) => (
            `Page: ${pageNumber}`
          )} fixed />
        </View>
      </Page>
    </Document>
  );
};

export default CanvassInsightsPDFDocument;
